import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateRole, useUpdateRole, useRole, useAvailablePermissions } from '../hooks/useRoles.ts';
import { ArrowLeft, Save, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { PermissionPage } from '../components/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

const RoleFormPage = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const isEditing = !!roleId;
  
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const { data: roleData, isLoading: isLoadingRole } = useRole(roleId);
  const { data: availablePermissions } = useAvailablePermissions();
  
  const [formData, setFormData] = useState({
    display_name: '',
    description: '',
    permission_ids: []
  });

  // Load role data when editing
  useEffect(() => {
    if (isEditing && roleData) {
      setFormData({
        display_name: roleData.display_name || '',
        description: roleData.description || '',
        permission_ids: roleData.permissions?.map(p => p.id) || []
      });
    }
  }, [roleData, isEditing]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId]
    }));
  };

  const handleSelectAllInModule = (module, permissions) => {
    const modulePermissionIds = permissions.map(p => p.id);
    const allSelected = modulePermissionIds.every(id => formData.permission_ids.includes(id));
    
    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected
        ? prev.permission_ids.filter(id => !modulePermissionIds.includes(id))
        : [...new Set([...prev.permission_ids, ...modulePermissionIds])]
    }));
  };

  const handleSelectAll = () => {
    const allPermissionIds = Object.values(groupedPermissions).flat().map(p => p.id);
    const allSelected = allPermissionIds.every(id => formData.permission_ids.includes(id));
    
    setFormData(prev => ({
      ...prev,
      permission_ids: allSelected ? [] : allPermissionIds
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }

    try {
      if (isEditing) {
        // Update existing role
        await updateRole.mutateAsync({
          id: parseInt(roleId),
          ...formData
        });
        toast.success('Role updated successfully');
      } else {
        // Create new role
        await createRole.mutateAsync(formData);
        toast.success('Role created successfully');
      }
      navigate('/roles');
    } catch (error) {
      const errorMessage = error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} role`;
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/roles');
  };

  // Group permissions by module
  const groupedPermissions = availablePermissions?.data || {};

  if (isEditing && isLoadingRole) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-500">Loading role...</p>
        </div>
      </div>
    );
  }

  if (isEditing && !roleData) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center py-8">
          <p className="text-gray-500">Role not found</p>
          <button
            onClick={handleCancel}
            className="mt-4 px-4 py-2 text-blue-600 hover:text-blue-800"
          >
            Back to Roles
          </button>
        </div>
      </div>
    );
  }

  const pageTitle = isEditing ? `Edit Role: ${roleData?.display_name}` : 'Create New Role';
  const submitButtonText = isEditing 
    ? (updateRole.isLoading ? 'Updating...' : 'Update Role')
    : (createRole.isLoading ? 'Creating...' : 'Create Role');
  const isLoading = isEditing ? updateRole.isLoading : createRole.isLoading;
  
  const requiredPermission = isEditing ? PERMISSIONS.EDIT_ROLE : PERMISSIONS.CREATE_ROLE;

  return (
    <PermissionPage permission={requiredPermission}>
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleCancel}
            className="flex items-center text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Roles
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit} className="p-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Group Administrator"
                required
              />
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe the role's purpose and responsibilities..."
              />
            </div>
          </div>

          {/* Permissions */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Permissions</h2>
              {availablePermissions?.data && (
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  {Object.values(groupedPermissions).flat().every(p => formData.permission_ids.includes(p.id))
                    ? 'Deselect All'
                    : 'Select All'
                  }
                </button>
              )}
            </div>
            
            {availablePermissions?.data ? (
              <div className="space-y-4">
                {Object.entries(groupedPermissions).map(([module, permissions]) => {
                  const modulePermissionIds = permissions.map(p => p.id);
                  const allSelectedInModule = modulePermissionIds.every(id => formData.permission_ids.includes(id));
                  const someSelectedInModule = modulePermissionIds.some(id => formData.permission_ids.includes(id));
                  
                  return (
                    <div key={module} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-md font-medium text-gray-800">{module}</h3>
                        <button
                          type="button"
                          onClick={() => handleSelectAllInModule(module, permissions)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          {allSelectedInModule ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {permissions.map((permission) => (
                          <label key={permission.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={formData.permission_ids.includes(permission.id)}
                              onChange={() => handlePermissionToggle(permission.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{permission.display_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-500">Loading permissions...</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center"
            >
              <X size={16} className="mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              <Save size={16} className="mr-2" />
              {submitButtonText}
            </button>
          </div>
        </form>
      </div>
    </div>
    </PermissionPage>
  );
};

export default RoleFormPage; 